import { supabase } from './supabase';

// Screen-open measurement (table app_events, migrations/sprint5.sql), and
// since sprint 7 the two beta milestones as well — see trackEvent below.
//
// One row per screen open: the screen's id, the user (filled by the column
// default auth.uid() — the client never names the user), and the time (the
// column default now()). Nothing else is sent, and the table has no column
// that could carry anything else. Rows are insert-only for the client; only
// service_role reads them, and a daily sweep deletes rows older than 90 days
// (David, 2026-09-18).
//
// Fire-and-forget: a failed insert is logged once and changes nothing the
// user sees. The same screen reopened within a couple of seconds (a re-render,
// a swipe that settles back) is one open, not two.

const SCREEN_RE = /^[a-z][a-z0-9_]{0,39}$/;   // the table's CHECK, mirrored
const DEDUP_MS  = 2000;

let last = { screen: null, at: 0 };
let warned = false;

export function trackScreen(screen) {
  if (!supabase) return;
  const id = String(screen || '').toLowerCase();
  if (!SCREEN_RE.test(id)) return;
  const now = Date.now();
  if (last.screen === id && now - last.at < DEDUP_MS) return;
  last = { screen: id, at: now };

  supabase.from('app_events').insert({ screen: id }).then(({ error }) => {
    if (error && !warned) {
      warned = true;
      console.warn('[appEvents] insert failed:', error.code, error.message);
    }
  }).catch(() => {});
}

// ─── Milestones (sprint 7) ───────────────────────────────────────────────────
// The beta is measured on four numbers (marketing roadmap): 1 signup
// completed · 2 first portfolio entered · 3 back on day 7 · 4 weekly return.
// 1 and 2 are rows here, in the same three-column table, under the same
// format check — `screen` holds an event id. 3 and 4 are derived from the
// screen-open rows that already exist and get no event of their own.
//
// `once` dedups per user per device (localStorage); a second device may write
// the row again, so the analyst counts DISTINCT user_id, never rows. The
// client cannot read the table back, so this is the only dedup it can do.
export const EVENTS = {
  SIGNUP_COMPLETED:        'signup_completed',
  FIRST_PORTFOLIO_ENTERED: 'first_portfolio_entered',
};

export function trackEvent(event, { once = null } = {}) {
  if (!supabase) return;
  const id = String(event || '').toLowerCase();
  if (!SCREEN_RE.test(id)) return;
  const key = once ? `si_ev_${id}_${once}` : null;
  try { if (key && localStorage.getItem(key)) return; } catch { /* private mode: no dedup, still counted */ }
  supabase.from('app_events').insert({ screen: id }).then(({ error }) => {
    if (error) {
      if (!warned) { warned = true; console.warn('[appEvents] insert failed:', error.code, error.message); }
      return;
    }
    try { if (key) localStorage.setItem(key, '1'); } catch { /* ignore */ }
  }).catch(() => {});
}
